import type {
  InjectionGuideRegister,
  MedicationCatalogue,
  MedicationCatalogueVersion,
  MedicationSourceCheckRun,
  MedicationSourceReviewTask,
  MedicationSourceReviewTasks,
  NewMedicationCatalogueVersion,
  ResolveMedicationSourceReview,
} from "@care-by-ella/contracts";
import { API_BASE, apiFetch, tenantHeader, withCredentials } from "./api";
import { jsonOr401 } from "./clinician-api";

// TES-1292 — the medication catalogue.

const BASE = `${API_BASE}/employee-api/v1/medication-catalogue`;

export async function fetchMedicationCatalogue(opts?: {
  history?: boolean;
}): Promise<MedicationCatalogue> {
  const res = await apiFetch(opts?.history ? `${BASE}?history=1` : BASE, {
    headers: tenantHeader(),
    ...withCredentials,
  });
  return jsonOr401(res);
}

// One fetch per page load for the pickers: every order surface on a page
// shares it. A failed fetch is forgotten, so the next picker tries again.
let pickerCatalogue: Promise<MedicationCatalogue> | null = null;
// The catalogue once it has loaded. The order's pure helpers
// (PrescriptionOrderFields' toOrder, orderComplete) read it, so a surface
// that sends an order needs no catalogue plumbing of its own. Null until it
// loads, or when it failed: the order is then free text, as before.
let loadedCatalogue: MedicationCatalogue | null = null;

export function pickerMedicationCatalogue(): Promise<MedicationCatalogue> {
  if (!pickerCatalogue) {
    pickerCatalogue = fetchMedicationCatalogue().then(
      (c) => {
        loadedCatalogue = c;
        return c;
      },
      (e: unknown) => {
        pickerCatalogue = null;
        throw e;
      },
    );
  }
  return pickerCatalogue;
}

export function loadedPickerCatalogue(): MedicationCatalogue | null {
  return loadedCatalogue;
}

/** For tests: the catalogue the order helpers read. */
export function rememberPickerCatalogue(c: MedicationCatalogue | null): void {
  loadedCatalogue = c;
}

/** After an edit on the catalogue screen, the pickers read it again. */
export function forgetPickerCatalogue(): void {
  pickerCatalogue = null;
}

async function post<T>(url: string, body?: unknown): Promise<T> {
  const res = await apiFetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...tenantHeader() },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    ...withCredentials,
  });
  forgetPickerCatalogue();
  return jsonOr401(res);
}

export function createMedicationCatalogueVersion(
  productId: string,
  body: NewMedicationCatalogueVersion,
): Promise<MedicationCatalogueVersion> {
  return post(`${BASE}/${encodeURIComponent(productId)}/versions`, body);
}

export function approveMedicationCatalogueVersion(
  versionId: string,
): Promise<MedicationCatalogueVersion> {
  return post(`${BASE}/versions/${encodeURIComponent(versionId)}/approve`);
}

export function markMedicationCatalogueVersionChecked(
  versionId: string,
): Promise<MedicationCatalogueVersion> {
  return post(`${BASE}/versions/${encodeURIComponent(versionId)}/checked`);
}

// TES-1293 — the weekly link check's internal review tasks.

export async function fetchSourceReviewTasks(opts?: {
  all?: boolean;
}): Promise<MedicationSourceReviewTasks> {
  const res = await apiFetch(
    opts?.all ? `${BASE}/review-tasks?all=1` : `${BASE}/review-tasks`,
    { headers: tenantHeader(), ...withCredentials },
  );
  return jsonOr401(res);
}

export function resolveSourceReviewTask(
  taskId: string,
  body: ResolveMedicationSourceReview,
): Promise<MedicationSourceReviewTask> {
  return post(
    `${BASE}/review-tasks/${encodeURIComponent(taskId)}/resolve`,
    body,
  );
}

// TES-1361 — the injection guide's source register (read only).
export async function fetchInjectionGuideRegister(): Promise<InjectionGuideRegister> {
  const res = await apiFetch(`${BASE}/guide-register`, {
    headers: tenantHeader(),
    ...withCredentials,
  });
  return jsonOr401(res);
}

export function runSourceCheckNow(): Promise<MedicationSourceCheckRun> {
  return post(`${BASE}/source-check`);
}
